import { Icon, Text } from '@/components'
import { DeviceLimitResult } from '@/app/pages/multi-program-enrollment/device-selection/DeviceLimitHelpers'
import { useNavigation } from '@/app/hooks'
import { useBottomSheet } from '@/context/bottom-sheet-context'
import useMultiProgramFlowControl from '@/hooks/useMultiProgramFlowControl'
import { UtilityProgramDeviceType } from '@/types/device'
import { ReactNode } from 'react'
import {
  CheckIcon,
  ChevronRightIcon,
  ThermometerIcon,
  ZapIcon,
} from 'lucide-react'
import { DeviceLimits } from '@/types/programs-new/programs-new'
import { match } from 'ts-pattern'
import clsx from 'clsx'

type Props = {
  deviceLimitsResult: DeviceLimitResult
  deviceLimits: DeviceLimits
  utilityProgramId: number
}

function useDeviceLimitConnectDeviceLink() {
  const { snapToHeader, closeBottomSheet } = useBottomSheet()
  const { setFlowStartPoint } = useMultiProgramFlowControl()
  const navigate = useNavigation()

  const routeToConnectDevice = (link: string) => {
    snapToHeader()
    closeBottomSheet()
    setFlowStartPoint()

    navigate.push(link)
  }

  return {
    routeToConnectDevice,
  }
}

export default function DeviceLimitsDisplay(props: Props) {
  return (
    <div className="flex flex-col justify-between h-full gap-5">
      <section className="[&>*:not(:last-child)]:border-b [&>*]:border-solid [&>*]:border-base-grey-100">
        {props.deviceLimits.thermostat?.min &&
          RequiredThermostatSection(props.deviceLimitsResult.thermostat)}
        {props.deviceLimits.vehicle?.min &&
          RequiredVehicleSection(
            props.deviceLimitsResult.vehicle,
            props.utilityProgramId,
          )}
        {props.deviceLimits.charger?.min &&
          RequiredChargerSection(props.deviceLimitsResult.charger)}
      </section>
    </div>
  )
}

const DeviceLimitSection = ({
  buttonProps,
  deviceLimitsMet,
  deviceType,
  icon,
}: {
  deviceLimitsMet: boolean
  deviceType: UtilityProgramDeviceType
  icon: ReactNode
  buttonProps: {
    onClick: () => void
  }
}) => {
  return (
    <div
      className={clsx(
        'flex flex-row gap-2 items-center justify-between py-4',
        !deviceLimitsMet
          ? 'hover:bg-base-grey-100 cursor-pointer rounded-lg'
          : '',
      )}
      onClick={deviceLimitsMet ? undefined : buttonProps.onClick}
    >
      <div className="flex items-center gap-2">
        {match(deviceLimitsMet)
          .with(false, () => (
            <div className="self-start text-themed-blue-500 bg-themed-blue-100 size-8 flex items-center justify-center rounded-lg">
              {icon}
            </div>
          ))
          .otherwise(() => (
            <div className="bg-base-green-100 text-base-green-700 items-center justify-center flex rounded-lg size-8">
              <CheckIcon />
            </div>
          ))}
        <Text variant="body1">
          {deviceLimitsMet
            ? `${deviceType} successfully connected`
            : `Connect your ${deviceType.toLowerCase()}`}
        </Text>
      </div>
      <div className="inline-flex items-center gap-1">
        {!deviceLimitsMet && (
          <div className="text-sm font-bold px-4 rounded-full bg-themed-purple-100 text-themed-purple-500 py-1">
            Required
          </div>
        )}
        {!deviceLimitsMet && (
          <ChevronRightIcon
            size={20}
            className="flex-none text-base-grey-400"
          />
        )}
      </div>
    </div>
  )
}

const RequiredThermostatSection = (deviceLimitsMet: boolean) => {
  const { routeToConnectDevice } = useDeviceLimitConnectDeviceLink()

  return (
    <DeviceLimitSection
      deviceType="Thermostat"
      icon={<ThermometerIcon />}
      deviceLimitsMet={deviceLimitsMet}
      buttonProps={{
        onClick: () =>
          routeToConnectDevice('/connect-device/thermostat?skipUpsell=true'),
      }}
    />
  )
}

const RequiredVehicleSection = (
  deviceLimitsMet: boolean,
  utilityProgramId: number,
) => {
  const { routeToConnectDevice } = useDeviceLimitConnectDeviceLink()
  return (
    <DeviceLimitSection
      deviceType="Vehicle"
      icon={<Icon name="Vehicle" />}
      deviceLimitsMet={deviceLimitsMet}
      buttonProps={{
        onClick: () =>
          routeToConnectDevice(
            '/connect-device/vehicle?value_prop=true&utilityProgramId=' +
              utilityProgramId,
          ),
      }}
    />
  )
}

const RequiredChargerSection = (deviceLimitsMet: boolean) => {
  const { routeToConnectDevice } = useDeviceLimitConnectDeviceLink()

  return (
    <DeviceLimitSection
      deviceType="Charger"
      icon={<ZapIcon />}
      deviceLimitsMet={deviceLimitsMet}
      buttonProps={{
        onClick: () => routeToConnectDevice('/connect-device/charger'),
      }}
    />
  )
}
